/**
 * Automation Module - Messages Automation Hook
 *
 * Backward-compatible wrapper that delegates to the new service hooks.
 * Existing components can continue using `useAutomation()` unchanged.
 */

import {
  useGetOnboarding,
  useGetAllAutomations,
  useGetListingManagement,
  useGetAutomationById,
  useGetMasterData,
  useGetUnSyncedProperties,
  useListNabeehServices,
  useListNabeehServiceCategories,
  useListChecklistItems,
} from '../services/automation.queries';
import {
  useGeneratePreDefinedAutomations,
  useUpdateActivity,
  useSetOnboardingComplete,
  useSyncProperty,
} from '../services/automation.mutations';
import type { ErrorTypeAutomation } from '../types';

const useAutomation = () => {
  const GetOnboardingQuery = () => useGetOnboarding();

  const GetAllAutomationsQuery = (data: {
    pageSize: string;
    pageNumber: string;
    search?: string;
  }) => useGetAllAutomations(data);

  const GetListingManagmentQuery = (data?: {
    pageSize?: number;
    pageNumber?: number;
  }) => useGetListingManagement(data);

  const GetAutomationByIdQuery = (data: { id: number }) =>
    useGetAutomationById(data);

  const GetMasterDataQuery = (key?: string) => useGetMasterData(key);

  const GetUnSyncedPropertiesQuery = () => useGetUnSyncedProperties();

  const ListNabeehServicesQuery = () => useListNabeehServices();

  const ListNabeehServiceCategoriesQuery = () => useListNabeehServiceCategories();

  const ListChecklistItemsQuery = (serviceId?: string) => useListChecklistItems(serviceId);

  const GeneratePreDefinedAutomationsMutate = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: ErrorTypeAutomation) => void;
  }) => useGeneratePreDefinedAutomations({ onSuccess, onError });

  const updateActivityMutate = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: ErrorTypeAutomation) => void;
  }) => useUpdateActivity({ onSuccess, onError });

  const SetOnboardingCompleteMutate = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: ErrorTypeAutomation) => void;
  }) => useSetOnboardingComplete({ onSuccess, onError });

  const SyncPropertyMutate = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: ErrorTypeAutomation) => void;
  }) => useSyncProperty({ onSuccess, onError });

  return {
    GetOnboardingQuery,
    GeneratePreDefinedAutomationsMutate,
    GetAllAutomationsQuery,
    GetListingManagmentQuery,
    updateActivityMutate,
    SetOnboardingCompleteMutate,
    GetAutomationByIdQuery,
    GetMasterDataQuery,
    GetUnSyncedPropertiesQuery,
    ListNabeehServicesQuery,
    ListNabeehServiceCategoriesQuery,
    ListChecklistItemsQuery,
    SyncPropertyMutate,
  };
};

export { useAutomation };
